'use client'

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

interface GeoFeature {
  properties: {
    label: string
    name: string
    postcode: string
    city: string
    type: string
  }
}

interface Props {
  defaultAddress?: string
  defaultCity?: string
  defaultPostalCode?: string
  required?: boolean
  fieldErrors?: { address?: string; city?: string; postalCode?: string }
}

export function AddressAutocomplete({
  defaultAddress = '',
  defaultCity = '',
  defaultPostalCode = '',
  required,
  fieldErrors = {},
}: Props) {
  const [postalCode, setPostalCode] = useState(defaultPostalCode)
  const [city, setCity] = useState(defaultCity)
  const [address, setAddress] = useState(defaultAddress)

  const [citySuggestions, setCitySuggestions] = useState<{ name: string; postcode: string }[]>([])
  const [addrSuggestions, setAddrSuggestions] = useState<GeoFeature[]>([])
  const [showCity, setShowCity] = useState(false)
  const [showAddr, setShowAddr] = useState(false)
  const [mounted, setMounted] = useState(false)

  const cityWrapRef = useRef<HTMLDivElement>(null)
  const addrWrapRef = useRef<HTMLDivElement>(null)
  const [cityStyle, setCityStyle] = useState<React.CSSProperties>({})
  const [addrStyle, setAddrStyle] = useState<React.CSSProperties>({})

  useEffect(() => { setMounted(true) }, [])

  // Close dropdowns on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (cityWrapRef.current && !cityWrapRef.current.contains(e.target as Node)) setShowCity(false)
      if (addrWrapRef.current && !addrWrapRef.current.contains(e.target as Node)) setShowAddr(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  // Postal code → city lookup
  useEffect(() => {
    if (postalCode.length !== 5) { setCitySuggestions([]); setShowCity(false); return }
    fetch(`https://api-adresse.data.gouv.fr/search/?q=${postalCode}&type=municipality&limit=10`)
      .then((r) => r.json())
      .then((data) => {
        const seen = new Set<string>()
        const cities = (data.features ?? [])
          .map((f: GeoFeature) => ({ name: f.properties.city, postcode: f.properties.postcode }))
          .filter((c: { name: string }) => { if (seen.has(c.name)) return false; seen.add(c.name); return true })
        setCitySuggestions(cities)
        if (cities.length === 1) {
          setCity(cities[0].name)
        } else if (cities.length > 1) {
          if (cityWrapRef.current) {
            const rect = cityWrapRef.current.getBoundingClientRect()
            setCityStyle({ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 9999 })
            setShowCity(true)
          }
        }
      })
      .catch(() => {})
  }, [postalCode])

  // Address autocomplete — debounced
  useEffect(() => {
    if (address.length < 3) { setAddrSuggestions([]); setShowAddr(false); return }
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ q: address, limit: '7' })
      if (postalCode.length === 5) params.set('postcode', postalCode)
      fetch(`https://api-adresse.data.gouv.fr/search/?${params}`)
        .then((r) => r.json())
        .then((data) => {
          const feats = data.features ?? []
          setAddrSuggestions(feats)
          if (feats.length > 0 && addrWrapRef.current) {
            const rect = addrWrapRef.current.getBoundingClientRect()
            setAddrStyle({ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 9999 })
            setShowAddr(true)
          }
        })
        .catch(() => {})
    }, 280)
    return () => clearTimeout(timer)
  }, [address, postalCode])

  function selectCity(c: { name: string; postcode: string }) {
    setCity(c.name)
    setPostalCode(c.postcode)
    setShowCity(false)
  }

  function selectAddress(feat: GeoFeature) {
    setAddress(feat.properties.name)
    setCity(feat.properties.city)
    setPostalCode(feat.properties.postcode)
    setShowAddr(false)
    setAddrSuggestions([])
  }

  const popStyle: React.CSSProperties = {
    background: 'rgba(10,10,20,0.96)',
    backdropFilter: 'blur(24px)',
  }

  return (
    <div className="space-y-4">
      {/* Code postal + Ville */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="glass-label">
            Code postal {required && <span className="text-danger">*</span>}
          </label>
          <input
            name="postalCode"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="01000"
            maxLength={5}
            className={`glass-input${fieldErrors.postalCode ? ' error' : ''}`}
          />
          {fieldErrors.postalCode && <p className="glass-field-error">{fieldErrors.postalCode}</p>}
        </div>

        <div ref={cityWrapRef} className="col-span-2">
          <label className="glass-label">
            Ville {required && <span className="text-danger">*</span>}
          </label>
          <input
            name="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onFocus={() => {
              if (citySuggestions.length > 1 && cityWrapRef.current) {
                const rect = cityWrapRef.current.getBoundingClientRect()
                setCityStyle({ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 9999 })
                setShowCity(true)
              }
            }}
            placeholder="Lyon"
            className={`glass-input${fieldErrors.city ? ' error' : ''}`}
            autoComplete="off"
          />
          {fieldErrors.city && <p className="glass-field-error">{fieldErrors.city}</p>}
          {mounted && showCity && citySuggestions.length > 0 && createPortal(
            <div
              className="rounded-xl overflow-hidden shadow-2xl border border-white/[0.15]"
              style={{ ...popStyle, ...cityStyle }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {citySuggestions.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectCity(c)}
                  className="w-full text-left px-3.5 py-2.5 text-sm text-text-primary hover:bg-white/[0.08] transition-colors border-b border-white/[0.06] last:border-0"
                >
                  {c.name}
                </button>
              ))}
            </div>,
            document.body,
          )}
        </div>
      </div>

      {/* Adresse */}
      <div ref={addrWrapRef}>
        <label className="glass-label">
          Adresse {required && <span className="text-danger">*</span>}
        </label>
        <input
          name="address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="12 rue de la Paix"
          className={`glass-input${fieldErrors.address ? ' error' : ''}`}
          autoComplete="off"
        />
        {fieldErrors.address && <p className="glass-field-error">{fieldErrors.address}</p>}
        {mounted && showAddr && addrSuggestions.length > 0 && createPortal(
          <div
            className="rounded-xl overflow-hidden shadow-2xl border border-white/[0.15]"
            style={{ ...popStyle, ...addrStyle }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {addrSuggestions.map((feat, i) => (
              <button
                key={i}
                type="button"
                onClick={() => selectAddress(feat)}
                className="w-full text-left px-3.5 py-2.5 hover:bg-white/[0.08] transition-colors border-b border-white/[0.06] last:border-0"
              >
                <p className="text-text-primary text-sm">{feat.properties.name}</p>
                <p className="text-text-muted text-xs">{feat.properties.postcode} {feat.properties.city}</p>
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>
    </div>
  )
}
